import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { CommunityMessageMedia } from '../../services/communityService';
import {
  getDocumentKind,
  getFileExtension,
  documentKindLabel,
  documentKindColor,
} from '../../utils/documentFileTypes';
import { formatFileSize } from '../../utils/formatFileSize';
import { CommunityMediaLightbox } from './CommunityMediaLightbox';

interface Props {
  media: CommunityMessageMedia;
  filename: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
}

export const DocumentMessageCard: React.FC<Props> = ({
  media,
  filename,
  href,
  onClick,
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const kind = getDocumentKind(filename, media.mime);
  const ext = getFileExtension(filename) || kind;
  const isImageDocument = kind === 'image' && Boolean(media.blob_url);
  const previewUrl =
    media.thumb_url ||
    (isImageDocument ? media.blob_url : undefined);

  const metaParts: string[] = [];
  if (media.page_count != null && media.page_count > 0) {
    metaParts.push(`${media.page_count} page${media.page_count === 1 ? '' : 's'}`);
  }
  const sizeLabel = formatFileSize(media.size_bytes);
  if (sizeLabel) metaParts.push(sizeLabel);
  if (ext) metaParts.push(ext);

  const openExternal = href && !isImageDocument;

  const handleCardClick = (e: React.MouseEvent) => {
    onClick?.(e);
    if (isImageDocument) {
      e.preventDefault();
      setLightboxOpen(true);
    }
  };

  return (
    <>
      <Box
        component={openExternal ? 'a' : 'div'}
        href={openExternal ? href : undefined}
        target={openExternal ? '_blank' : undefined}
        rel={openExternal ? 'noopener noreferrer' : undefined}
        onClick={handleCardClick}
        sx={{
          display: 'block',
          textDecoration: 'none',
          color: 'inherit',
          minWidth: 220,
          maxWidth: 320,
          cursor: isImageDocument || openExternal ? 'pointer' : 'default',
        }}
      >
        {previewUrl && (
          <Box
            component="img"
            src={previewUrl}
            alt=""
            sx={{
              width: '100%',
              maxHeight: 140,
              objectFit: 'cover',
              borderRadius: '8px 8px 0 0',
              display: 'block',
              bgcolor: '#f1f3f4',
            }}
          />
        )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          pt: previewUrl ? 1 : 0,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 44,
            borderRadius: 0.5,
            bgcolor: documentKindColor(kind),
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: '0.62rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
          }}
        >
          {documentKindLabel(kind)}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 500,
              color: '#0f0f0f',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {filename}
          </Typography>
          {metaParts.length > 0 && (
            <Typography sx={{ fontSize: '0.72rem', color: '#9aa0a6', mt: 0.25 }}>
              {metaParts.join(' • ')}
            </Typography>
          )}
        </Box>
      </Box>
      </Box>

      {isImageDocument && media.blob_url && (
        <CommunityMediaLightbox
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          mediaUrl={media.blob_url}
          mediaType="image"
        />
      )}
    </>
  );
};
